"use client";

import { Braces, Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/atoms/tooltip";
import { cn } from "@/lib/utils";

export type McpUnoptimizedMatchCardProps = {
  serverCount: number;
  toolCount: number;
  onShow?: () => void;
  className?: string;
};

function plural(count: number, label: string) {
  return `${count} ${label}${count === 1 ? "" : "s"}`;
}

// Shared explanation for both the desktop-hover and mobile-tap tooltips.
const TOOLTIP = (
  <>
    Tools built around individual actions rather than full tasks. Useful when
    you need something specific, but your agent often has to combine a few to
    complete a task.
  </>
);

export function McpUnoptimizedMatchCard({
  serverCount,
  toolCount,
  onShow,
  className,
}: McpUnoptimizedMatchCardProps) {
  if (serverCount === 0 && toolCount === 0) {
    return null;
  }

  const count = [
    serverCount > 0 ? `+${plural(serverCount, "server")}` : null,
    toolCount > 0 ? `+${plural(toolCount, "tool")}` : null,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={cn(
        "flex h-full w-full items-center gap-2 rounded-lg border border-brand-text/40 border-dashed bg-muted/20 p-3",
        className
      )}
      data-slot="mcp-unoptimized-match-card"
    >
      {/*
       * Main action: reveals unoptimized results. Desktop hover on this
       * button shows the tooltip explanation via pointer-enter.
       */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger
            render={
              <button
                aria-label={`Show unoptimized (${count})`}
                className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-inset"
                onClick={onShow}
                type="button"
              />
            }
          >
            <Braces aria-hidden className="size-5 shrink-0 text-brand-text" />
            <span className="min-w-0 flex-1 truncate font-medium text-sm">
              Show unoptimized
            </span>
            <span className="shrink-0 whitespace-nowrap text-muted-foreground text-xs tabular-nums">
              {count}
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs" side="top">
            {TOOLTIP}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/*
       * Mobile-only info button. Hidden on sm+ where the hover tooltip covers
       * the explanation. Sibling to the action button so tapping it never
       * fires onShow.
       */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger
            render={
              <button
                aria-label="What are unoptimized tools?"
                className="shrink-0 cursor-pointer rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 sm:hidden"
                type="button"
              />
            }
          >
            <Info aria-hidden className="size-3.5 text-muted-foreground" />
          </TooltipTrigger>
          <TooltipContent className="max-w-xs" side="top">
            {TOOLTIP}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}
